import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { z } from 'zod';
import { ApiError } from '../../api/client';
import { useLogin } from '../../api/queries';
import { Alert, Button, Field, Input, PasswordInput } from '../../components/ui';
import { errorMessage } from '../../i18n/labels';
import { AuthLayout, TermsNotice } from './AuthLayout';
import { fieldError } from './fieldError';
import { OAuthButtons, OrDivider } from './OAuthButtons';
import { loginSchema } from './schemas';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const login = useLogin();
  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });
  const oauthError = params.get('error');
  const rawNext = params.get('next');

  const onSubmit = form.handleSubmit((values) =>
    login.mutate(values, {
      onSuccess: (res) => {
        // Without 2FA, RequireGuest follows `next` once the session is signed in.
        if (res.mfaRequired) {
          const q = rawNext ? `?next=${encodeURIComponent(rawNext)}` : '';
          void navigate(`/two-factor${q}`, { replace: true });
        }
      },
    }),
  );
  const notVerified =
    login.error instanceof ApiError && login.error.code === 'AUTH_EMAIL_NOT_VERIFIED';

  return (
    <AuthLayout
      title={t('auth.loginTitle')}
      subtitle={t('auth.loginSubtitle')}
      footer={
        <>
          {t('auth.noAccount')}{' '}
          <Link to="/signup" className="link">
            {t('auth.signUp')}
          </Link>
        </>
      }
    >
      {oauthError && !login.error && (
        <Alert tone="danger" className="mb-4">
          {errorMessage(t, new ApiError(400, oauthError, oauthError))}
        </Alert>
      )}
      {login.error && (
        <Alert tone="danger" className="mb-4">
          {errorMessage(t, login.error)}
          {notVerified && (
            <Link
              to={`/verify-email?email=${encodeURIComponent(form.getValues('email'))}`}
              className="link ml-1"
            >
              {t('auth.resendVerification')}
            </Link>
          )}
        </Alert>
      )}
      <OAuthButtons />
      <OrDivider />
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Field label={t('auth.email')} error={fieldError(t, form.formState.errors.email?.message)}>
          {(p) => <Input type="email" autoComplete="email" {...p} {...form.register('email')} />}
        </Field>
        <Field
          label={t('auth.password')}
          error={fieldError(t, form.formState.errors.password?.message)}
        >
          {(p) => (
            <PasswordInput autoComplete="current-password" {...p} {...form.register('password')} />
          )}
        </Field>
        <div className="flex justify-end">
          <Link to="/forgot-password" className="link text-xs">
            {t('auth.forgotPassword')}
          </Link>
        </div>
        <Button type="submit" className="w-full" loading={login.isPending}>
          {t('auth.signIn')}
        </Button>
      </form>
      <TermsNotice />
    </AuthLayout>
  );
}
